import { useState } from "react";
import { FiDownload, FiArrowUp, FiArrowDown } from "react-icons/fi";
import { ProfileComparisonEntry } from "../../../api/scenarios";

interface ScenarioComparisonTableProps {
  profiles: ProfileComparisonEntry[];
  scenarioName?: string;
}

type SortKey = "profile_label" | "generation_mwh" | "dalys" | "pdfs" | "usd" | "co2e_kg";

const formatNumber = (v: number | null, decimals = 0) => {
  if (v == null) return "-";
  return v.toLocaleString(undefined, { maximumFractionDigits: decimals });
};

const formatUsd = (v: number | null) => {
  if (v == null) return "-";
  if (Math.abs(v) >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}M`;
  if (Math.abs(v) >= 1_000) return `$${(v / 1_000).toFixed(1)}K`;
  return `$${v.toFixed(0)}`;
};

export function ScenarioComparisonTable({
  profiles,
  scenarioName,
}: ScenarioComparisonTableProps) {
  const [sortKey, setSortKey] = useState<SortKey>("profile_label");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const sorted = [...profiles].sort((a, b) => {
    const aVal = a[sortKey];
    const bVal = b[sortKey];
    if (aVal == null && bVal == null) return 0;
    if (aVal == null) return 1;
    if (bVal == null) return -1;
    const cmp = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
    return sortDir === "asc" ? cmp : -cmp;
  });

  const exportCsv = () => {
    const header = "Profile,Generation (MWh),DALYs,PDFs,USD,CO2e (kg),Status";
    const rows = sorted.map(
      (p) =>
        `${p.profile_label},${p.generation_mwh ?? ""},${p.dalys ?? ""},${p.pdfs ?? ""},${p.usd ?? ""},${p.co2e_kg ?? ""},${p.status ?? ""}`,
    );
    const csv = [header, ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `profile-comparison${scenarioName ? `-${scenarioName}` : ""}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const SortIcon = ({ col }: { col: SortKey }) => {
    if (sortKey !== col) return null;
    return sortDir === "asc" ? (
      <FiArrowUp className="inline w-3 h-3 ml-0.5" />
    ) : (
      <FiArrowDown className="inline w-3 h-3 ml-0.5" />
    );
  };

  const thCls =
    "pb-2 pr-4 font-medium text-left cursor-pointer hover:text-text-primary transition-colors select-none";

  return (
    <div className="rounded-lg border border-border bg-surface-700 p-4">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-semibold text-text-primary">
          {scenarioName ? `${scenarioName} — ` : ""}Profile Comparison
        </h4>
        <button
          onClick={exportCsv}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-quantum-400 border border-quantum-500/30 rounded-lg hover:bg-quantum-500/10 transition-colors"
        >
          <FiDownload className="w-3.5 h-3.5" />
          Export CSV
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-text-secondary">
              <th className={thCls} onClick={() => handleSort("profile_label")}>
                Profile <SortIcon col="profile_label" />
              </th>
              <th className={thCls} onClick={() => handleSort("generation_mwh")}>
                Generation <SortIcon col="generation_mwh" />
              </th>
              <th className={thCls} onClick={() => handleSort("dalys")}>
                DALYs <SortIcon col="dalys" />
              </th>
              <th className={thCls} onClick={() => handleSort("pdfs")}>
                PDFs <SortIcon col="pdfs" />
              </th>
              <th className={thCls} onClick={() => handleSort("usd")}>
                USD <SortIcon col="usd" />
              </th>
              <th className={thCls} onClick={() => handleSort("co2e_kg")}>
                CO2e (tonnes) <SortIcon col="co2e_kg" />
              </th>
              <th className="pb-2 font-medium text-left text-text-secondary">Status</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((p) => (
              <tr
                key={p.profile_label}
                className="border-b border-border/50 hover:bg-surface-600 transition-colors"
              >
                <td className="py-2.5 pr-4 font-medium text-text-primary">
                  {p.profile_label}
                </td>
                <td className="py-2.5 pr-4 text-text-secondary">
                  {formatNumber(p.generation_mwh)}
                </td>
                <td className="py-2.5 pr-4 text-text-secondary">
                  {formatNumber(p.dalys, 1)}
                </td>
                <td className="py-2.5 pr-4 text-text-secondary">
                  {p.pdfs != null ? p.pdfs.toFixed(4) : "-"}
                </td>
                <td className="py-2.5 pr-4 text-text-secondary">
                  {formatUsd(p.usd)}
                </td>
                <td className="py-2.5 pr-4 text-text-secondary">
                  {p.co2e_kg != null ? formatNumber(p.co2e_kg / 1000, 1) : "-"}
                </td>
                <td className="py-2.5">
                  <span
                    className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                      p.status === "completed"
                        ? "bg-green-500/10 text-green-400"
                        : p.status === "running" || p.status === "queued"
                          ? "bg-yellow-500/10 text-yellow-400"
                          : p.status === "failed"
                            ? "bg-red-500/10 text-red-400"
                            : "bg-gray-500/10 text-gray-400"
                    }`}
                  >
                    {p.status || "pending"}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
